import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CameraIcon } from "lucide-react";
import { useAccount } from "@/api/auth/queries";
import { useProfile } from "./useProfile";
import AssetUploader from "@/components/upload";
import { IFile } from "@/types/upload";
import { UpdateUser } from "@/api/workspace/types";
import { useQueryClient } from "@tanstack/react-query";
import { ACCOUNT } from "@/api/auth/types";

export default function Profile() {
  const queryClient = useQueryClient();
  const { data: account, isLoading, refetch } = useAccount();
  const { updateUser } = useProfile();
  const [avatar, setAvatar] = useState({ url: "", id: 0 });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");


  const clearPasswordFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const handleAvatarChange = (file: IFile) => {
    if (file?.url) {
      setAvatar({ url: file.url, id: Number(file.id) });
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(event.target.value);
  };

  const handleSubmit = async () => {
    const updateData: UpdateUser = {
      name: name || account?.name || '',
      email: email || account?.email || '',
      avatar: avatar.id 
    };

    if (newPassword && confirmNewPassword) {
      updateData.new_password1 = newPassword;
      updateData.new_password2 = confirmNewPassword;
      if (account?.has_password) {
        updateData.old_password = currentPassword;
      }
    }

    const response = await updateUser(updateData);
    
    if (response) {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT }); 
      await refetch();
      clearPasswordFields();
    }
  };

  useEffect(() => {
    if (account?.avatar) {
      setAvatar({ url: account.avatar, id: Number(account.avatar) });
    }
    clearPasswordFields();
  }, [account]);
 
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center space-y-2">
        <Avatar className="h-24 w-24">
          <AvatarImage
            src={avatar.url || "/placeholder.svg?height=100&width=100"}
            alt="Profile picture"
          />
          <AvatarFallback>
            {account?.name.charAt(0).toUpperCase()}
            {account?.name.charAt(1).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="relative">
          <AssetUploader
            pure
            onChange={handleAvatarChange}
            fileTypes={["PNG", "JPEG", "JPG"]}>
            <p className="flex items-center justify-center cursor-pointer text-sm text-muted-foreground hover:text-foreground">
              <CameraIcon className="w-4 h-4 mr-2" />
              Change Picture
            </p>
          </AssetUploader>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          isLoading={isLoading}
          defaultValue={account?.name}
          id="name"
          placeholder="Enter your name"
          onChange={(event) => handleInputChange(event, setName)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          isLoading={isLoading}
          defaultValue={account?.email}
          id="email"
          type="email"
          placeholder="Enter your email"
          onChange={(event) => handleInputChange(event, setEmail)}
        />
      </div>
      {account?.has_password && (
        <div className="space-y-2">
          <Label htmlFor="current-password">Current Password</Label>
          <Input
            isLoading={isLoading}
            id="current-password"
            type="password"
            placeholder="Enter your current password"
            onChange={(e) => handleInputChange(e, setCurrentPassword)}
            value={currentPassword}
          />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="new-password">New Password</Label>
        <Input
          isLoading={isLoading}
          id="new-password"
          type="password"
          placeholder="Enter your new password"
          onChange={(e) => handleInputChange(e, setNewPassword)}
          value={newPassword}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirm-password">Confirm New Password</Label>
        <Input
          isLoading={isLoading}
          id="confirm-password"
          type="password"
          placeholder="Confirm your new password"
          onChange={(e) => handleInputChange(e, setConfirmNewPassword)}
          value={confirmNewPassword}
        />
      </div>

      <Button
        isLoading={isLoading}
        className="w-full mt-4"
        disabled={isLoading}
        onClick={handleSubmit}>
        Save Changes
      </Button>
    </div>
  );
}